/** SUNTRONIX ONE — CRM Customers (Vtiger-style account list).
 *  Route: /commercial/crm/customers — search, classify, add, preview.
 *  Phase 1 of the Dealer / Customer / Individual channel framework:
 *  relationship / entity filters, bulk Classify + XLSX import. */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { OrgName, CityPin } from "@/components/identity/Identity";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AddCustomerModal from "@/components/app/AddCustomerModal";
import { MasterActions } from "@/components/app/MasterActions";
import { Building2, PlusCircle, Search, Tags, Upload } from "lucide-react";

const FILTERS = [
  { key: "ALL", label: "All", params: {} },
  { key: "DEALERS", label: "Dealers", params: { relationship: "DEALER / RESELLER" } },
  { key: "END", label: "End Customers", params: { relationship: "END CUSTOMER" } },
  { key: "INDIVIDUALS", label: "Individuals", params: { entity: "INDIVIDUAL" } },
  { key: "PROSPECTS", label: "Prospects", params: { relationship: "PROSPECT" } },
  { key: "DISTRIBUTORS", label: "Distributors", params: { relationship: "DISTRIBUTOR" } },
  { key: "UNCLASSIFIED", label: "Unclassified", params: { relationship: "UNCLASSIFIED" } },
];

function relBadge(rel) {
  if (!rel) return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-slate-50 text-slate-500 border-slate-200 whitespace-nowrap">UNCLASSIFIED</span>;
  const r = rel;
  const cls = r.includes("DEALER") ? "bg-indigo-50 text-indigo-700 border-indigo-200"
    : r === "PROSPECT" ? "bg-amber-50 text-amber-700 border-amber-200"
    : r === "DISTRIBUTOR" ? "bg-purple-50 text-purple-700 border-purple-200"
    : r === "PARTNER" ? "bg-teal-50 text-teal-700 border-teal-200"
    : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${cls}`}>{r}</span>;
}

export default function CrmCustomersList() {
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [intelSeed, setIntelSeed] = useState(null); // { q, detection } from NEW CUSTOMER DETECTED
  const [needsPush, setNeedsPush] = useState(false);
  const [filter, setFilter] = useState(() => localStorage.getItem("crm_cust_filter") || "ALL");
  const [selected, setSelected] = useState(() => new Set());
  const [classifyOpen, setClassifyOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const load = (query = q, np = needsPush, fk = filter) => {
    const f = FILTERS.find((x) => x.key === fk) || FILTERS[0];
    return api.get("/vtiger/crm-customers", { params: { q: query || undefined, needs_push: np || undefined, limit: 300, ...f.params } })
      .then(({ data }) => { setRows(data.items || []); setTotal(data.total || 0); })
      .catch(() => toast.error("Could not load customers"));
  };

  useEffect(() => {
    load("");
    if (searchParams.get("new")) {
      setAddOpen(true);
      if (searchParams.get("q") || searchParams.get("detection")) setIntelSeed({ q: searchParams.get("q") || "", detection: searchParams.get("detection") || null });
      searchParams.delete("new"); searchParams.delete("q"); searchParams.delete("detection");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q, needsPush, filter), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, needsPush, filter]);

  const pickFilter = (key) => { setFilter(key); localStorage.setItem("crm_cust_filter", key); setSelected(new Set()); };
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOnPage = (rows || []).map((r) => r.id);
  const allSelected = allOnPage.length > 0 && allOnPage.every((id) => selected.has(id));
  const toggleAll = () => setSelected(() => allSelected ? new Set() : new Set(allOnPage));

  const filtered = rows;

  return (
    <div className="max-w-[1300px] mx-auto px-4 py-3" data-testid="crm-customers-page">
      <div className="flex flex-wrap items-center gap-3 mb-2.5">
        <Building2 className="h-4 w-4 text-slate-500" />
        <h1 className="text-lg font-semibold text-slate-900">Customers</h1>
        {rows && <span className="text-[11px] text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{filtered?.length ?? 0} of {total}</span>}
                <div className="relative ml-auto w-full sm:w-72">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9"
            placeholder="Search name, code, GSTIN, city, phone…"
            data-testid="crm-customers-search" />
        </div>
        <Button variant="outline" className="h-9" onClick={() => setImportOpen(true)} data-testid="crm-classify-import-btn">
          <Upload className="h-4 w-4 mr-1.5" /> Import classification
        </Button>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white h-9"
          onClick={() => setAddOpen(true)} data-testid="new-customer-btn">
          <PlusCircle className="h-4 w-4 mr-1.5" /> New Customer
        </Button>
      </div>

      {/* ── Channel classification filters ── */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="crm-customer-filters">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => pickFilter(f.key)}
            data-testid={`crm-filter-${f.key.toLowerCase()}`}
            className={`text-[12px] rounded-full px-3 py-1 border transition-colors ${
              filter === f.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Bulk classify toolbar ── */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-3 px-3 py-2 rounded-md bg-indigo-50 border border-indigo-200" data-testid="crm-bulk-toolbar">
          <Tags className="h-4 w-4 text-indigo-600" />
          <span className="text-[13px] text-indigo-900 font-medium">{selected.size} selected</span>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white h-8"
            onClick={() => setClassifyOpen(true)} data-testid="crm-bulk-classify-btn">Classify selected</Button>
          <button onClick={() => setSelected(new Set())} className="text-[12px] text-indigo-700 hover:underline ml-1">Clear</button>
        </div>
      )}

      {!filtered ? (
        <div className="space-y-2 py-2">
          {[...Array(8)].map((_, i) => <div key={i} className="h-10 bg-slate-50 rounded animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-md px-4 py-8 text-center" data-testid="crm-customers-empty">
          No customers in the “{(FILTERS.find((x) => x.key === filter) || FILTERS[0]).label}” view{q ? ` matching “${q}”` : ""}.
          {filter !== "ALL" && <button onClick={() => pickFilter("ALL")} className="ml-1 text-slate-600 font-medium hover:underline">Show all</button>}
          <button onClick={() => setAddOpen(true)} className="ml-1 text-emerald-700 font-medium hover:underline">+ Add Customer</button>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-md overflow-x-auto bg-white">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="crm-select-all" />
                </th>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Relationship</th>
                <th className="px-3 py-2 hidden lg:table-cell">Segment</th>
                <th className="px-3 py-2 hidden md:table-cell">City</th>
                <th className="px-3 py-2 hidden md:table-cell" title="Where this customer record originated; SUNTRONIX customer code is authoritative">Source</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 300).map((r) => (
                <tr key={r.id} onClick={() => navigate(`/commercial/crm/customers/${r.id}`)}
                  data-testid={`crm-customer-row-${r.id}`}
                  className="border-b border-slate-50 hover:bg-emerald-50/40 cursor-pointer">
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)}
                      data-testid={`crm-select-${r.id}`} />
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{r.customer_code}</td>
                  <td className="px-3 py-2 text-slate-900">
                    <OrgName name={r.account_name} testid={`crm-name-${r.id}`} />
                    {r.entity_type === "INDIVIDUAL" && <span className="ml-1.5 text-[10px] text-slate-400">(Individual)</span>}
                  </td>
                  <td className="px-3 py-2">{relBadge(r.sales_relationship)}</td>
                  <td className="px-3 py-2 hidden lg:table-cell text-slate-600">{r.customer_segment || "—"}</td>
                  <td className="px-3 py-2 hidden md:table-cell text-slate-600">{r.billing_city ? <CityPin city={r.billing_city} pin={r.billing_pincode || r.billing_pin} /> : "—"}</td>
                  <td className="px-3 py-2 hidden md:table-cell text-[11px] text-slate-500" data-testid={`crm-source-${r.id}`}>{r.vtiger_id ? "Imported" : "ONE"}</td>
                  <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <MasterActions type="customer" id={r.id} name={r.account_name}
                      active={r.active !== false} testid={`crm-customer-${r.id}`}
                      onEdit={() => navigate(`/commercial/crm/customers/${r.id}`)}
                      onChanged={() => load()} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {addOpen && (
        <AddCustomerModal onClose={() => { setAddOpen(false); setIntelSeed(null); }} initialQuery={intelSeed?.q || ""}
          onCreated={(c) => { setAddOpen(false); load(); toast.success("Customer added to master"); if (intelSeed?.detection) api.post(`/customer-intel/detections/${intelSeed.detection}/resolve`, null, { params: { customer_id: c?.id } }).catch(() => {}); setIntelSeed(null); }} />
      )}
      {classifyOpen && (
        <ClassifyDialog ids={[...selected]} onClose={() => setClassifyOpen(false)}
          onDone={() => { setClassifyOpen(false); setSelected(new Set()); load(); }} />
      )}
      {importOpen && (
        <ImportDialog onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />
      )}
    </div>
  );
}

function ClassifyDialog({ ids, onClose, onDone }) {
  const [opts, setOpts] = useState({ sales_relationship: [], customer_segment: [] });
  const [rel, setRel] = useState("");
  const [seg, setSeg] = useState("");
  const [ent, setEnt] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/vtiger/crm-customers-classification-options").then(({ data }) => setOpts(data)).catch(() => {}); }, []);
  const apply = async () => {
    if (!rel && !seg && !ent) { toast.error("Choose at least one field to set"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/vtiger/crm-customers/classify", {
        ids, sales_relationship: rel || null, customer_segment: seg || null, customer_type: ent || null,
      });
      toast.success(`Classified ${data.updated} customer(s)`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Classify failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose} data-testid="crm-classify-dialog">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold">Classify {ids.length} customer(s)</div>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>
        <div className="p-5 space-y-3">
          <div><Label>Sales relationship</Label>
            <select value={rel} onChange={(e) => setRel(e.target.value)} data-testid="classify-relationship"
              className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
              <option value="">— Leave unchanged —</option>
              {(opts.sales_relationship || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select></div>
          <div><Label>Customer segment</Label>
            <select value={seg} onChange={(e) => setSeg(e.target.value)} data-testid="classify-segment"
              className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
              <option value="">— Leave unchanged —</option>
              {(opts.customer_segment || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select></div>
          <div><Label>Entity type</Label>
            <select value={ent} onChange={(e) => setEnt(e.target.value)} data-testid="classify-entity"
              className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white">
              <option value="">— Leave unchanged —</option>
              <option value="ORGANISATION">Organisation</option>
              <option value="INDIVIDUAL">Individual</option>
            </select></div>
        </div>
        <div className="p-4 border-t border-slate-200 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={apply} disabled={busy} data-testid="classify-apply">
            {busy ? "Applying…" : "Apply"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ImportDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (commit) => {
    if (!file) { toast.error("Choose an XLSX or CSV file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/vtiger/crm-customers/classify/import?commit=${commit}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      if (commit) { toast.success(`Applied to ${data.applied} customer(s)`); onDone(); }
      else { setPreview(data); toast.success(`${data.matched} matched · ${data.unmatched.length} unmatched`); }
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose} data-testid="crm-import-dialog">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-semibold">Bulk classify from XLSX / CSV</div>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[12px] text-slate-500">
            Columns (any order): <span className="font-mono">customer_code</span> or <span className="font-mono">account_name</span>,
            plus <span className="font-mono">sales_relationship</span>, <span className="font-mono">customer_segment</span>,
            <span className="font-mono"> entity_type</span>. Existing customers are matched and updated — no new records are created.
          </p>
          <input type="file" accept=".xlsx,.csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
            data-testid="crm-import-file" className="text-[13px]" />
          {preview && (
            <div className="text-[12px] bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="crm-import-preview">
              <div className="font-medium text-slate-700">{preview.matched} match(es) · {preview.unmatched.length} unmatched</div>
              {preview.preview.slice(0, 6).map((p, i) => (
                <div key={i} className="text-slate-600 mt-0.5">{p.code} · {p.name} → {p.sales_relationship || ""} {p.customer_segment || ""} {p.customer_type || ""}</div>
              ))}
              {preview.unmatched.length > 0 && (
                <div className="text-rose-600 mt-1">Unmatched: {preview.unmatched.slice(0, 5).map((u) => u.code || u.name).join(", ")}{preview.unmatched.length > 5 ? "…" : ""}</div>
              )}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-slate-200 flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => send(false)} disabled={busy || !file} data-testid="crm-import-preview-btn">
            {busy ? "…" : "Preview"}
          </Button>
          <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => send(true)} disabled={busy || !file} data-testid="crm-import-apply-btn">
            {busy ? "Applying…" : "Apply classification"}
          </Button>
        </div>
      </div>
    </div>
  );
}
